import React, { useState, useEffect, useRef, useCallback } from 'react';
import useDraggable from '../hooks/useDraggable';
import { useTranslation } from '../i18n/LanguageContext';

// ── Renk Kodlaması ────────────────────────────────────────────
const PREFIX_COLORS = {
  '[Analysis]':  '#2563eb', // mavi
  '[Bot]':       '#059669', // yeşil
  '[Trade]':     '#b45309', // kahve/altın
  '[Trail]':     '#c2410c', // turuncu
  '[Watch]':     '#7c3aed', // mor
  '[Reconcile]': '#64748b', // gri
  '[Sync]':      '#0f766e', // teal
  '[DB]':        '#94a3b8', // açık gri
  '[App]':       '#475569', // slate
  '[Auth]':      '#4f46e5', // indigo
};

function getColor(msg, level) {
  if (level === 'error') return '#dc2626';
  for (const [prefix, color] of Object.entries(PREFIX_COLORS)) {
    if (msg.includes(prefix)) return color;
  }
  return '#64748b';
}

// ── Filtre Kategorileri ───────────────────────────────────────
const getFilters = (t) => [
  { id: 'all',      label: t('logViewer.filterAll'),     match: () => true },
  { id: 'analysis', label: t('logViewer.filterAnalysis'),   match: m => m.includes('[Analysis]') },
  { id: 'trade',    label: t('logViewer.filterTrade'),    match: m => m.includes('[Trade]') || m.includes('[Trail]') || m.includes('[Sync]') },
  { id: 'bot',      label: t('logViewer.filterBot'),      match: m => m.includes('[Bot]') || m.includes('[Watch]') },
  { id: 'reconcile',label: t('logViewer.filterReconcile'),match: m => m.includes('[Reconcile]') },
  { id: 'error',    label: t('logViewer.filterError'),     match: (m, l) => l === 'error' || m.toLowerCase().includes('hata') || m.toLowerCase().includes('error') },
];

function formatTs(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString('tr-TR', { hour12: false });
}

export default function LogViewer({ token, onClose }) {
  const { t } = useTranslation();
  const FILTERS = getFilters(t);
  const { offset: dragOffset, onMouseDown: handleHeaderMouseDown } = useDraggable();
  const [logs,       setLogs]       = useState([]);
  const [filter,     setFilter]     = useState('all');
  const [search,     setSearch]     = useState('');
  const [autoScroll, setAutoScroll] = useState(true);
  const [paused,     setPaused]     = useState(false);
  const scrollRef  = useRef(null);
  const pausedRef  = useRef(false);

  pausedRef.current = paused;

  // İlk yüklemede mevcut buffer'ı çek
  useEffect(() => {
    fetch('/api/logs', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setLogs(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [token]);

  // WS mesajlarını dinle (App.js'deki WS'i doğrudan kullanamayız, kendi WS açıyoruz)
  useEffect(() => {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws    = new WebSocket(`${proto}://${window.location.host}/ws`);   // oturum çerezi (madde 17)

    ws.onmessage = ({ data: raw }) => {
      try {
        const msg = JSON.parse(raw);
        if (msg.type === 'log') {
          if (!pausedRef.current) {
            setLogs(prev => {
              const next = [...prev, msg.data];
              return next.length > 600 ? next.slice(-600) : next;
            });
          }
        }
      } catch (_) {}
    };

    return () => ws.close();
  }, [token]);

  // Auto-scroll
  useEffect(() => {
    if (!autoScroll || !scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [logs, autoScroll]);

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const atBottom = scrollHeight - scrollTop - clientHeight < 40;
    if (atBottom && autoScroll === false) setAutoScroll(true);
    if (!atBottom && autoScroll === true) setAutoScroll(false);
  }, [autoScroll]);

  // Filtre fonksiyonu
  const activeFilter = FILTERS.find(f => f.id === filter);
  const visible = logs.filter(entry => {
    if (!activeFilter.match(entry.msg, entry.level)) return false;
    if (search.trim()) return entry.msg.toLowerCase().includes(search.toLowerCase());
    return true;
  });

  return (
    <div className="log-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="log-modal" style={{ transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)` }}>

        {/* Başlık */}
        <div className="log-header" onMouseDown={handleHeaderMouseDown}>
          <div className="log-title-row">
            <span className="log-title">{t('logViewer.title')}</span>
            <span className="log-count">{t('logViewer.lineCount', { visible: visible.length, total: logs.length })}</span>
          </div>
          <button className="settings-close" onClick={onClose}>✕</button>
        </div>

        {/* Araç Çubuğu */}
        <div className="log-toolbar">
          <div className="log-filters">
            {FILTERS.map(f => (
              <button
                key={f.id}
                className={`log-filter-btn ${filter === f.id ? 'active' : ''}`}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="log-search-row">
            <input
              className="log-search"
              placeholder={t('logViewer.searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <button
              className={`log-pause-btn ${paused ? 'paused' : ''}`}
              onClick={() => setPaused(p => !p)}
              title={paused ? t('logViewer.resume') : t('logViewer.pause')}
            >
              {paused ? '▶' : '⏸'}
            </button>
            <button
              className="log-clear-btn"
              onClick={() => setLogs([])}
              title={t('logViewer.clear')}
            >
              {t('logViewer.clearButton')}
            </button>
          </div>
        </div>

        {/* Log Listesi */}
        <div
          className="log-body"
          ref={scrollRef}
          onScroll={handleScroll}
        >
          {visible.length === 0 ? (
            <div className="log-empty">{t('logViewer.empty')}</div>
          ) : (
            visible.map((entry, i) => (
              <div key={i} className="log-line">
                <span className="log-ts">{formatTs(entry.ts)}</span>
                <span
                  className="log-msg"
                  style={{ color: getColor(entry.msg, entry.level) }}
                >
                  {entry.msg}
                </span>
              </div>
            ))
          )}
        </div>

        {/* Auto-scroll göstergesi */}
        {!autoScroll && (
          <button
            className="log-scroll-btn"
            onClick={() => {
              setAutoScroll(true);
              if (scrollRef.current)
                scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            }}
          >
            {t('logViewer.scrollToBottom')}
          </button>
        )}
      </div>
    </div>
  );
}
